// brain.ts — the AI client. Calls the LIVE Oriora server brain for this app
//   https://api.orioralabs.com/api/apps/expense_audit_ai/{structure,categorize,query,summary,anomalies}
// with the customer's Oriora session token. The server holds the prompts + bills the
// Oriora wallet (Managed AI / BYOK). Expense data stays local; only the scoped text
// for a given call is sent.

import { invoke } from "./tauri-web";
import { getAccessToken } from "./supabase";
import type { ExpenseFilter } from "./store";

const SERVER = "https://api.orioralabs.com";

export type BrainErrorCode = "insufficient_funds" | "managed_ai_blocked" | "unauthenticated" | "unavailable" | "no_store" | "too_large";
export class BrainError extends Error {
  code: BrainErrorCode;
  constructor(code: BrainErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = "BrainError";
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// SSE reader for the streaming fleet chat (request body {stream:true} → text/event-stream):
// `: open` / `: keepalive` comments and [DONE] are ignored, each `data: {"chunk":…}` event
// feeds onChunk, and the `data: {"done":true, …}` event carries the exact old JSON payload —
// returned where res.json() used to be, so all existing action/reply mapping stays untouched.
async function readSseChat(bodyStream: ReadableStream<Uint8Array>, onChunk: (t: string) => void): Promise<Record<string, unknown>> {
  const reader = bodyStream.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done: Record<string, unknown> | null = null;
  for (;;) {
    const { value, done: eof } = await reader.read();
    if (eof) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) !== -1) {
      const line = buf.slice(0, nl).replace(/\r$/, "");
      buf = buf.slice(nl + 1);
      if (!line.startsWith("data:")) continue; // comment (": open"/": keepalive") or blank line
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") continue;
      let ev: Record<string, unknown>;
      try { ev = JSON.parse(payload) as Record<string, unknown>; } catch { continue; }
      if (typeof ev.chunk === "string") onChunk(ev.chunk);
      else if (ev.done) done = ev;
    }
  }
  if (!done) throw new BrainError("unavailable", "The reply stream ended early — please try again.");
  // A streamed reply can end with a billing/auth error instead of a payload ({done:true, error, _status}).
  // Returning it as data dissolved the "Top up" message into an empty action (found live 2026-08-27) —
  // throw the same BrainError the buffered path would.
  if (typeof done.error === "string" && done.error) {
    const st = typeof done._status === "number" ? done._status : 0;
    const msg = typeof done.message === "string" && done.message ? done.message : done.error;
    if (st === 401) throw new BrainError("unauthenticated", msg);
    if (st === 402) throw new BrainError("insufficient_funds", msg);
    if (st === 403) throw new BrainError("managed_ai_blocked", msg);
    throw new BrainError("unavailable", msg);
  }
  return done;
}

export async function brainCall<T>(endpoint: string, body: unknown, onChunk?: (t: string) => void): Promise<T> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  // Retry transient failures (502/503/504 + network) — at volume these happen; a single
  // bad gateway shouldn't lose a receipt. Hard errors (401/402/403) never retry.
  const MAX = 3;
  let lastTransient = "AI is temporarily unavailable.";
  for (let attempt = 1; attempt <= MAX; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${SERVER}/api/apps/expense_audit_ai/${endpoint}`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(onChunk && body && typeof body === "object" ? { ...(body as Record<string, unknown>), stream: true } : body),
      });
    } catch {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", lastTransient);
    }
    if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
    if (res.status === 402) throw new BrainError("insufficient_funds", "AI is paused — add funds to continue.");
    if (res.status === 403) throw new BrainError("managed_ai_blocked", (await res.json().catch(() => null))?.message || "Connect a vendor key in Oriora settings to use AI for this app.");
    if ([502, 503, 504].includes(res.status)) {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is busy — please try again.");
    }
    if (!res.ok) throw new BrainError("unavailable", "AI request failed.");
    // Streaming fleet chat: with onChunk the body carries stream:true and the server answers
    // as SSE — read it incrementally (readSseChat above); the done event IS the old JSON
    // payload. Anything that is not an event stream (old server, an error status body, a
    // proxy that buffered it away) falls through to the buffered path — never a dead end.
    if (onChunk && res.body && (res.headers.get("content-type") ?? "").includes("text/event-stream")) {
      return (await readSseChat(res.body, onChunk)) as unknown as T;
    }
    return (await res.json()) as T;
  }
  throw new BrainError("unavailable", lastTransient);
}

// Strip a reasoning <think>…</think> channel, then extract the JSON object.
export function parseJsonLoose<T>(text: string): T {
  const afterThink = text.includes("</think>") ? text.slice(text.lastIndexOf("</think>") + 8) : text;
  const fenced = afterThink.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const raw = (fenced ? fenced[1] : afterThink).trim();
  const a = raw.indexOf("{"), b = raw.lastIndexOf("}");
  if (a === -1 || b === -1) throw new Error("no_json_in_response");
  return JSON.parse(raw.slice(a, b + 1)) as T;
}

export interface StructuredReceipt {
  vendor: string;
  amount_cents: number;
  date_on: string;
  currency: string;
  category: string;
  confidence: number;
}

export async function structureReceipt(ocrText: string): Promise<StructuredReceipt> {
  const r = await brainCall<Partial<StructuredReceipt>>("structure", { ocr_text: ocrText });
  return {
    vendor: String(r.vendor ?? "Unknown"),
    amount_cents: Number.isFinite(Number(r.amount_cents)) ? Math.max(0, Math.round(Number(r.amount_cents))) : 0,
    date_on: typeof r.date_on === "string" ? r.date_on : new Date().toISOString().slice(0, 10),
    currency: typeof r.currency === "string" ? r.currency : "USD",
    category: String(r.category ?? "Other"),
    confidence: Number.isFinite(Number(r.confidence)) ? Number(r.confidence) : 0.5,
  };
}

export async function suggestCategory(input: { vendor: string; amount_cents: number; notes?: string | null; categories: string[] }): Promise<{ category: string; confidence: number }> {
  const r = await brainCall<{ result?: string }>("categorize", { vendor: input.vendor, amount_cents: input.amount_cents, notes: input.notes ?? "" });
  const j = parseJsonLoose<{ category?: string; confidence?: number }>(r.result ?? "");
  const category = input.categories.includes(String(j.category)) ? String(j.category) : "Other";
  const confidence = Number.isFinite(Number(j.confidence)) ? Math.min(1, Math.max(0, Number(j.confidence))) : 0.5;
  return { category, confidence };
}

export async function parseExpenseQuery(query: string, categories: string[], today: string): Promise<ExpenseFilter> {
  const r = await brainCall<{ filter?: string }>("query", { q: query, categories, today });
  return parseJsonLoose<ExpenseFilter>(r.filter ?? "");
}

// Strip a reasoning <think>…</think> channel from plain-text output (summary isn't JSON,
// so parseJsonLoose doesn't run; a routed reasoning model can still prepend its scratchpad).
function stripThink(t: string): string {
  const s = t.includes("</think>") ? t.slice(t.lastIndexOf("</think>") + 8) : t;
  return s.replace(/^\s*<think>[\s\S]*$/i, "").trim() || s.trim();
}

// Throws (BrainError) on a real failure so the caller can show a retry instead of a silent
// blank panel. Returns null only for the legitimately-empty case (no expenses this month).
export async function monthSummary(expensesText: string, monthLabel: string): Promise<string | null> {
  if (!expensesText.trim()) return null;
  const r = await brainCall<{ summary?: string }>("summary", { expenses_text: expensesText, month: monthLabel });
  const clean = r.summary ? stripThink(r.summary) : null;
  return clean || null;
}

export interface Anomaly { expense_id: string; reason: string }

// Throws (BrainError) on a real failure. Returns [] for the legitimately-empty case.
export async function findAnomalies(expensesText: string, validIds: Set<string>): Promise<Anomaly[]> {
  if (!expensesText.trim()) return [];
  const r = await brainCall<{ anomalies?: string }>("anomalies", { expenses_text: expensesText });
  if (!r.anomalies) return [];
  const j = parseJsonLoose<{ anomalies?: Anomaly[] }>(r.anomalies);
  return (j.anomalies ?? [])
    .filter((a) => a && typeof a.expense_id === "string" && validIds.has(a.expense_id))
    .map((a) => ({ expense_id: a.expense_id, reason: String(a.reason ?? "flagged").slice(0, 200) }));
}

// Login opens the ecosystem web login in an in-app window (mirrors the website — email code +
// Google/GitHub); the open_login_window Rust command handles the session return + the
// Google/GitHub system-browser handoff. Session lands via the deep link (App.tsx).
// ── Cloud backup (the user owns their data) — fleet standard ────────────────────
export interface CloudBackupState { connected: boolean; provider?: string; data?: unknown }
async function authedFetch(endpoint: string, init?: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  return fetch(`${SERVER}/api/apps/expense_audit_ai/${endpoint}`, {
    ...init,
    headers: { ...(init?.headers ?? {}), authorization: `Bearer ${token}` },
  });
}
/** Save the local backup envelope to the user's connected cloud store (GitHub/Drive). */
export async function cloudBackupSave(envelope: unknown): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("backup", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ data: envelope }) });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to back up to the cloud.");
  // 413 = over the standard path's size limit (photo-heavy backup). Not retryable — the caller
  // switches to the device-direct GitHub save, or tells the customer how to enable it.
  if (res.status === 413) throw new BrainError("too_large", "This backup is too large for the standard path.");
  if (!res.ok) throw new BrainError("unavailable", "Cloud backup failed — please try again.");
  return (await res.json()) as { provider: string };
}
/** Save arbitrary text — a note the user dictates, or the whole conversation — to the user's OWN
 *  cloud store (their GitHub/Drive). Honors an explicit destination when the user names one. Oriora
 *  retains nothing (text relay only). Throws no_store when nothing is connected. */
export async function cloudStoreSave(
  content: string,
  opts?: { name?: string; kind?: string; destination?: "github" | "gdrive" },
): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("store", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ content, name: opts?.name, kind: opts?.kind, provider: opts?.destination }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to save to the cloud.");
  if (!res.ok) throw new BrainError("unavailable", "Saving to your cloud failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Read the cloud backup for restore. `connected:false` when no store is connected.
 *  Pass `provider` to read a NAMED source (the RESTORE STORY's explicit restore) — the server
 *  409s rather than silently reading a different cloud than the one the customer asked for. */
export async function cloudBackupLoad(provider?: "github" | "gdrive"): Promise<CloudBackupState> {
  let res: Response;
  try { res = await authedFetch(provider ? `backup?provider=${provider}` : "backup"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", `${provider === "gdrive" ? "Google Drive" : "GitHub"} isn't connected in your Oriora account.`);
  if (!res.ok) throw new BrainError("unavailable", "Couldn't read your cloud backup.");
  return (await res.json()) as CloudBackupState;
}

/** The RESTORE STORY status line — which clouds are connected, which one backups WRITE to, and the
 *  dated backup each holds. Ground truth from the relay (CUSTOMER-CLOUD-STORAGE.md §THE RESTORE STORY). */
export interface CloudBackupSlot { backup_exists: boolean; updated_at: string | null; bytes: number | null }
export interface CloudBackupStatusInfo {
  connected: ("github" | "gdrive")[];
  writes_to: "github" | "gdrive" | null;
  slots: Partial<Record<"github" | "gdrive", CloudBackupSlot>>;
}
export async function cloudBackupStatus(): Promise<CloudBackupStatusInfo> {
  let res: Response;
  try { res = await authedFetch("backup/status"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (!res.ok) throw new BrainError("unavailable", "Couldn't check your backup status.");
  return (await res.json()) as CloudBackupStatusInfo;
}

export const openWebLogin = () => invoke<void>("open_login_window");
